import { createSelector } from '@reduxjs/toolkit'
import { t } from 'i18next'
import { SpotType } from '@/models/cart'
import { APISectorDetailKeyType, MapSetupItemKeyName } from '@/models/map'
import { cartSlice } from '@/store/cartSlice'
import { selectTotalGlobalSelectedQuantity } from '@/store/cartSlice/selectors/selectTotalGlobalSelectedQuantity'
import { dayDifferenceSelector } from '@/store/insertPeriodSlice'
import { licenseSlice } from '@/store/licenseSlice'
import {
  mapSelectedElementSetupDataSelector,
  mapSelectors,
} from '@/store/mapSlice'
import { servicesAsRecordSelector } from '@/store/selectors/servicesAsRecordSelector'
import { warehouseSelectedElementAvailabilitySelector } from '@/store/selectors/warehouseSelectedElementAvailabilitySelector'
import {
  INFINITY_FALLBACK_VALUE,
  spotTypesWithoutSetupSeats,
} from '@/utils/constants'
import { getGroupBoughtQuantity } from '@/utils/getGroupBoughtQuantity'
import { getServiceMinimumQuantity } from '@/utils/getServiceMinimumQuantity'
import { getServiceSelectedQuantity } from '@/utils/getServiceSelectedQuantity'
import { sumServiceGroupSelectedQuantity } from '@/utils/sumServiceGroupSelectedQuantity'
import { activeElementQuotedSeatsAmountSelector } from './activeElementQuotedSeatsAmountSelector'
import { totalGlobalBoughtQuantitySelector } from './totalGlobalBoughtQuantitySelector'

export const selectCartQuoteErrorMessage = createSelector(
  [
    mapSelectedElementSetupDataSelector,
    activeElementQuotedSeatsAmountSelector,
    servicesAsRecordSelector,
    cartSlice.selectors.cartReservations,
    cartSlice.selectors.selectedElementQuotableProducts,
    licenseSlice.selectors.license,
    mapSelectors.selectedElement,
    dayDifferenceSelector,
    selectTotalGlobalSelectedQuantity,
    warehouseSelectedElementAvailabilitySelector,
    totalGlobalBoughtQuantitySelector,
  ],
  (
    mapData,
    selectedElementQuotedSeatsAmount,
    serviceRecord,
    cartReservations,
    selectedElementQuotableProducts,
    license,
    selectedElement,
    daysDiff,
    totalGlobalSelectedQuantity,
    warehouseAvailability,
    totalGlobalBoughtQuantity
  ): string[] => {
    const errors: string[] = []
    if (selectedElement === null) {
      return errors
    }
    const selectedElementSetupLimit = mapData?.selectedElementSetupLimit

    const isMaximumSeatAmountRespected =
      !selectedElementSetupLimit ||
      selectedElementQuotedSeatsAmount <=
        selectedElementSetupLimit[APISectorDetailKeyType.MAX]

    const isMinimumSeatAmountRespected =
      !selectedElementSetupLimit ||
      selectedElementQuotedSeatsAmount >=
        selectedElementSetupLimit[APISectorDetailKeyType.MIN]

    const isElementWithoutSeats = spotTypesWithoutSetupSeats.includes(
      selectedElement?.subType as SpotType
    )

    const cartItemsLimit =
      license?.bookingUserUmbrellasLimit || INFINITY_FALLBACK_VALUE

    const cartItemsAmount = Object.values(cartReservations).length
    const isMaximumCartItemsReached = cartItemsAmount === cartItemsLimit

    for (const serviceKey in selectedElementQuotableProducts.boughtServices) {
      const serviceId = Number(serviceKey)

      const service = serviceRecord[serviceId]

      if (!service) {
        // Cannot happen, but who knows
        throw Error(`Cannot find service with ID: ${serviceId}`)
      }

      let serviceAvailableQnt = Infinity
      let groupAvailableQnt = Infinity
      let globalAvailableQnt = Infinity
      const minQuantity = getServiceMinimumQuantity(
        service,
        selectedElementQuotedSeatsAmount,
        daysDiff
      )
      const totalServiceSelectedQuantity = getServiceSelectedQuantity({
        cartReservations,
        selectedElement,
        selectedElementQuotableProducts,
        serviceId: service.serviceId,
        ticketServices: Object.values(serviceRecord),
      })
      if (service.scheduleType === 'daily' && minQuantity > 0) {
        serviceAvailableQnt = Math.min(
          service.availableQuantity,
          (service.maximumQuantity > 0 ? service.maximumQuantity : Infinity) -
            service.reservedQuantity -
            totalServiceSelectedQuantity
        )
      }
      // If there is a group we also consider global and group limits
      if ((service.groupMaximumQnt ?? 0) > 0) {
        const totalGroupBoughtQuantity = getGroupBoughtQuantity({
          groupId: service.serviceGroupId,
          selectedElementQuotableProducts,
          ticketServices: Object.values(serviceRecord),
        })

        const totalGroupSelectedQuantity = sumServiceGroupSelectedQuantity({
          cartReservations,
          selectedElement,
          selectedElementQuotableProducts,
          service,
          ticketServices: Object.values(serviceRecord),
        })

        const updatedGlobalAvailability =
          (service.globalAvailableQnt ?? 0) - totalGlobalBoughtQuantity

        const updatedGroupAvailability =
          (service.groupAvailableQnt ?? 0) - totalGroupBoughtQuantity

        const updatedGlobalQnt =
          (service.globalReservedQnt ?? 0) + totalGlobalSelectedQuantity

        const updatedGroupQnt =
          (service.groupReservedQnt ?? 0) + totalGroupSelectedQuantity

        groupAvailableQnt = Math.min(
          updatedGroupAvailability,
          (service.groupMaximumQnt ?? 0) - updatedGroupQnt
        )
        globalAvailableQnt = Math.min(
          updatedGlobalAvailability,
          (service.globalMaximumQnt ?? 0) - updatedGlobalQnt
        )
      }

      // First check on service, then group, then global
      if (serviceAvailableQnt < 0) {
        errors.push(
          t('map.errors.maxDailyItemsLimitExceeded', {
            serviceName: service.name,
          })
        )
        break
      }
      if (groupAvailableQnt < 0) {
        errors.push(
          t('map.elementDetail.serviceGroupLimitReached', {
            groupName: service.serviceGroupName,
          })
        )
        break
      }
      if (globalAvailableQnt < 0) {
        errors.push(t('map.elementDetail.serviceGlobalLimitReached'))
        break
      }
    }

    if (isMaximumCartItemsReached) {
      errors.push(t('map.errors.mapSpotsLimitExceeded'))
    } else if (!isMaximumSeatAmountRespected) {
      errors.push(t('map.errors.invalidSetupConfiguration'))
    } else if (!isMinimumSeatAmountRespected && isElementWithoutSeats) {
      //please note: this is necessary when an element of type boat/parking/playfield
      // has been created in a sector requires a minimum number of seats but does not
      errors.push(t('map.errors.invalidSetupConfiguration'))
    }

    if (license?.warehouseOnline) {
      const visibleStagingItems =
        mapData?.selectedElementSetupItems.filter((i) => i.max > 0) ?? []

      visibleStagingItems.forEach((stagingItem) => {
        switch (stagingItem.keyName) {
          case MapSetupItemKeyName.BED: {
            if (warehouseAvailability.beds < 0) {
              errors.push(t('map.errors.warehouse.beds'))
            }
            break
          }
          case MapSetupItemKeyName.CHAIR: {
            if (warehouseAvailability.chairs < 0) {
              errors.push(t('map.errors.warehouse.chairs'))
            }
            break
          }
          case MapSetupItemKeyName.DECK_CHAIR: {
            if (warehouseAvailability.deckChairs < 0) {
              errors.push(t('map.errors.warehouse.deckChairs'))
            }
            break
          }
          case MapSetupItemKeyName.MAXI_BED: {
            if (warehouseAvailability.maxiBeds < 0) {
              errors.push(t('map.errors.warehouse.maxiBeds'))
            }
            break
          }
        }
      })
    }

    return errors
  }
)
